import { useState, useEffect } from 'react'
import AuthService from 'services/auth'
import cutileiApi from 'services/cutileiApi'
import { logApiError } from 'services/apiError'
import { Dialog, DialogBody, DialogContent, DialogHeader, DialogTitle } from '@cutilei/ui'
import Address from 'services/Address'
import CustomMessageService from 'services/CustomMessage'
import { ReactComponent as Loading } from 'icons/loading2.svg'
import { DateTime } from 'luxon'

import {
  Container,
  Column,
  SubTitle,
  InfoText,
  MessageBlock
} from './styles'
import MainButton from 'components/Buttons/MainButton'

const { formatPrice } = CustomMessageService

const getScheduleDateTime = schedule => (
  DateTime.fromFormat (`${schedule.date} ${schedule.time}`, 'yyyy-MM-dd H:mm')
)

/*
  O site que abre o link de confirmação. É o de produção só no build de produção; nos outros,
  inclusive rodando local, é o de staging: o WhatsApp não transforma `localhost` em link, e o
  staging usa o mesmo banco do desenvolvimento, então o agendamento abre lá. É a mesma regra
  do `siteUrl` dos apps.
*/
const confirmationSiteUrl = (import.meta.env.REACT_APP_ENVIRONMENT || 'production') === 'production'
  ? 'https://cutilei.com'
  : 'https://staging.cutilei.com'

export default function SendCustomMessagesModal ({
  visible,
  customer,
  schedule,
  onClose: handleClose
}) {
  const [loading, setLoading] = useState (true)
  const [customMessages, setCustomMessages] = useState ([])
  const [customerSchedules, setCustomerSchedules] = useState ([])
  const businessId = AuthService.getBusinessId ()
  const token = AuthService.getToken ()
  const firstName = customer?.name?.split (' ')[0]

  const requestConfig = {
    headers: {
      'Authorization': `Bearer ${token}`
    }
  }

  useEffect (() => {
    if (visible) getData ()
  }, [visible])

  const getData = async () => {
    setLoading (true)

    const [customMessages, customerSchedules] = await Promise.all ([
      getCustomMessages (),
      getCustomerSchedules ()
    ])

    setCustomMessages (customMessages)
    setCustomerSchedules (customerSchedules)
    setLoading (false)
  }

  const getCustomMessages = async () => {
    try {
      const { data: messages } = await cutileiApi.get (
        `/businesses/${businessId}/custom_messages`,
        requestConfig
      )

      return messages
    } catch (error) {
      logApiError (error)
      return []
    }
  }

  /*
    Os agendamentos do cliente no dia vêm da API, e não da lista de quem abriu este modal:
    ela muda conforme a tela (no app é só a coluna do profissional, na semana da web é um
    profissional só), e o "Pé" marcado com outra profissional ficaria fora da mensagem.

    A consulta não manda `canceled`, `status` nem `query`: a API lê os três como filtro de
    listagem e cobraria do profissional outra permissão. Cancelados já ficam de fora assim.
    Profissional sem a permissão da ficha do cliente (ou sem `agenda.all`, se for membro)
    recebe só os agendamentos dele, e só esses entram na mensagem.
  */
  const getCustomerSchedules = async () => {
    try {
      const customerFilter = schedule.customer
        ? `customer_id=${schedule.customer.id}`
        : `member_id=${schedule.member.id}`

      const { data: schedulesResponse } = await cutileiApi.get (
        `/schedules?business_id=${businessId}&date=${schedule.date}&${customerFilter}`
        + '&active=true&page=1&per_page=50',
        requestConfig
      )

      return schedulesResponse.data
    } catch (error) {
      logApiError (error)
      return []
    }
  }

  if (!customer) return null

  /*
    A lista fica no state de uma abertura para a outra, então é conferida de novo contra o
    agendamento aberto: até a busca nova chegar, ela pode ser de outro cliente.
  */
  const daySchedules = [
    schedule,
    ...customerSchedules.filter (s => (
      s.id !== schedule.id
      && s.date === schedule.date
      && s.business.id === schedule.business.id
      && s.customer?.id === schedule.customer?.id
      && s.member?.id === schedule.member?.id
    ))
  ].sort ((a, b) => getScheduleDateTime (a) - getScheduleDateTime (b))

  const formattedDate = DateTime.fromISO (schedule.date).toFormat ('dd/MM/yyyy')
  const address = Address.formatAddress (schedule.business.addresses[0])
  const pricedSchedules = daySchedules.filter (s => !s.cutilei_schedule)
  const [firstPricedSchedule, ...otherPricedSchedules] = pricedSchedules
  const totalPrice = pricedSchedules.reduce ((total, s) => total + parseFloat (s.price), 0)

  /*
    Um link só confirma todos: o primeiro agendamento vai no caminho, como sempre foi, e os
    demais em `?schedules=`, que a página de confirmação da web lê.

    O endereço é o do ambiente, e não um fixo: fixo em cutilei.com, o link de um agendamento
    de staging ou local abria a produção, que não tem esse agendamento, e a página ficava em
    branco.
  */
  const confirmationLink = `${confirmationSiteUrl}/schedules/${firstPricedSchedule?.id}/confirm`
    + (otherPricedSchedules.length > 0 ? `?schedules=${otherPricedSchedules.map (s => s.id).join (',')}` : '')

  const scheduleConfirmationMessage = daySchedules.length === 1 ? {
    title: 'Confirmação de agendamento',
    content:
      `Olá, ${firstName}! tudo bem? Estamos entrando em contato para confirmar seu agendamento conosco.\n\n`
      + 'Confirme a seguir os dados de seu agendamento:\n\n'
      + `- *Serviço:* ${schedule.service.name}\n`
      + `- *Profissional:* ${schedule.professional.nickname}\n`
      + `- *Data:* ${formattedDate}\n`
      + `- *Horário:* ${schedule.time}\n`
      + `- *Tolerância de atraso:* ${schedule.max_delay} minutos\n`
      + (!schedule.cutilei_schedule ? (
        `- *Valor do serviço:* R$${formatPrice (schedule.price)}\n\n`
        + 'Para confirmar seu agendamento, acesse o link abaixo:\n\n'
        + `${confirmationLink}\n\n`
      ) : '')
      + 'E aqui está o nosso endereço:\n\n'
      + `${address}\n\n`
      + 'Qualquer dúvida, estamos à disposição! Agradecemos por escolher nossos serviços e esperamos vê-lo em breve.'
  } : {
    title: 'Confirmação de agendamentos',
    content:
      `Olá, ${firstName}! tudo bem? Estamos entrando em contato para confirmar seus agendamentos conosco.\n\n`
      + `Confirme a seguir os dados de seus agendamentos do dia ${formattedDate}:\n\n`
      + daySchedules.map (s => (
        `*${s.service.name}*\n`
        + `- *Profissional:* ${s.professional.nickname}\n`
        + `- *Horário:* ${s.time}\n`
        + `- *Tolerância de atraso:* ${s.max_delay} minutos\n`
        + (!s.cutilei_schedule ? `- *Valor do serviço:* R$${formatPrice (s.price)}\n` : '')
      )).join ('\n') + '\n'
      + (pricedSchedules.length > 1 ? `*Valor total:* R$${formatPrice (totalPrice)}\n\n` : '')
      + (pricedSchedules.length > 0 ? (
        'Para confirmar seus agendamentos, acesse o link abaixo:\n\n'
        + `${confirmationLink}\n\n`
      ) : '')
      + 'E aqui está o nosso endereço:\n\n'
      + `${address}\n\n`
      + 'Qualquer dúvida, estamos à disposição! Agradecemos por escolher nossos serviços e esperamos vê-lo em breve.'
  }

  const noContentMessage = {
    title: 'Continuar sem mensagem personalizada',
    content: ''
  }

  const messages = [scheduleConfirmationMessage, ...customMessages, noContentMessage]

  /*
    As variáveis são trocadas no clique, com os agendamentos do cliente no dia. A confirmação já
    vem preenchida e passa sem mudança.
  */
  const sendMessage = message => {
    const encodedText = encodeURIComponent(
      CustomMessageService.fillVariables(message.content, { customer, schedules: daySchedules })
    )

    window.open(
      `https://api.whatsapp.com/send?phone=55${customer.phone}&text=${encodedText}`,
      '_blank',
      'noopener,noreferrer'
    )
    handleClose()
  }

  return (
    <Dialog open={visible} onOpenChange={open => { if (!open) handleClose() }}>
      <DialogContent size='md' aria-describedby={undefined}>
        <DialogHeader>
          <DialogTitle>Enviar mensagem para {customer.name}</DialogTitle>
        </DialogHeader>
        <DialogBody>
          {loading ? <Loading/> : (
            <Container>
              {!customer.phone ? (
                <>
                  <InfoText>
                    {'Este cliente não possui um número de celular cadastrado. '
                    + 'Por favor, atualize o cadastro do cliente para poder enviar mensagens.'}
                  </InfoText>
                  <MainButton
                    label='Ok'
                    onClick={handleClose}
                    width={100}
                    style={{marginTop: 20}}
                  />
                </>
              ) : (
                <>
                  <InfoText>
                    {'Selecione uma mensagem personalizada para enviar para o cliente. '
                    + 'As mensagens personalizadas são uma forma prática de se comunicar com os clientes, '
                    + 'permitindo que você envie informações relevantes de forma rápida e eficiente.'}
                  </InfoText>
                  <Column>
                    {messages.map ((message, index) => (
                      <MessageBlock
                        key={index}
                        onClick={() => sendMessage (message)}
                        style={{margin: 0}}
                      >
                        <SubTitle>{message.title}</SubTitle>
                      </MessageBlock>
                    ))}
                  </Column>
                </>
              )}
            </Container>
          )}
        </DialogBody>
      </DialogContent>
    </Dialog>
  )
}
